import {EnrichedAstNode, AstPosition, AstPositionWithMarginAndBorder} from '../types';

export type Gap = {
    start: number;
    end: number;
};

export function getTLPoint(position: AstPosition) {
    return {x: position.absoluteX, y: position.absoluteY};
}

export function getTRPoint(position: AstPosition) {
    return {
        x: position.absoluteX + position.width,
        y: position.absoluteY,
    };
}

export function getBLPoint(position: AstPosition) {
    return {
        x: position.absoluteX,
        y: position.absoluteY + position.height,
    };
}

export function getBRPoint(position: AstPosition) {
    return {
        x: position.absoluteX + position.width,
        y: position.absoluteY + position.height,
    };
}

export function getXBoundsWithMargin(position: AstPositionWithMarginAndBorder) {
    return {
        start: position.absoluteX - position.marginLeft,
        end: position.absoluteX + position.width + position.marginRight,
    };
}

export function getYBoundsWithMargin(position: AstPositionWithMarginAndBorder) {
    return {
        start: position.absoluteY - position.marginTop,
        end: position.absoluteY + position.height + position.marginBottom,
    };
}

export function getXBounds(position: AstPosition) {
    return {
        start: position.absoluteX,
        end: position.absoluteX + position.width,
    };
}

export function getYBounds(position: AstPosition) {
    return {
        start: position.absoluteY,
        end: position.absoluteY + position.height,
    };
}

export function getDistanceFromBounds(bounds: Gap | undefined) {
    if (!bounds) {
        return 0;
    }

    return bounds.end - bounds.start;
}

export function getCssValueFromBounds(bounds: Gap | undefined) {
    if (!bounds) {
        return 0;
    }

    return bounds.end - bounds.start;
}

export function getGroupDimensions(nodes: EnrichedAstNode[]) {
    // Draw a bounding box around the group nodes. This will be the max dims on each side.

    if (nodes.length === 0) {
        return {
            zIndex: 0,
            position: {
                width: 0,
                height: 0,
                absoluteX: 0,
                absoluteY: 0,
            },
            innerPosition: {
                width: 0,
                height: 0,
                absoluteX: 0,
                absoluteY: 0,
            },
        };
    }

    let topBound = nodes[0].position.absoluteY;
    let bottomBound = nodes[0].position.absoluteY + nodes[0].position.height;
    let leftBound = nodes[0].position.absoluteX;
    let rightBound = nodes[0].position.absoluteX + nodes[0].position.width;

    let zIndex = nodes[0].zIndex;

    nodes.forEach((node) => {
        const {position} = node;
        topBound = topBound < position.absoluteY ? topBound : position.absoluteY;
        bottomBound =
            bottomBound > position.absoluteY + position.height ? bottomBound : position.absoluteY + position.height;

        leftBound = leftBound < position.absoluteX ? leftBound : position.absoluteX;
        rightBound =
            rightBound > position.absoluteX + position.width ? rightBound : position.absoluteX + position.width;

        zIndex = node.zIndex > zIndex ? node.zIndex : zIndex;
    });

    return {
        zIndex,
        position: {
            width: rightBound - leftBound,
            height: bottomBound - topBound,
            absoluteX: leftBound,
            absoluteY: topBound,
        },
        innerPosition: {
            width: rightBound - leftBound,
            height: bottomBound - topBound,
            absoluteX: leftBound,
            absoluteY: topBound,
        },
    };
}

export function invertGapArray(gaps: Gap[]) {
    const groupsArray: {start: number; end: number}[] = [];

    gaps.forEach((gap, i) => {
        if (i === 0) {
            return;
        }
        groupsArray.push({start: gaps[i - 1].end, end: gap.start});
    });

    return groupsArray;
}

export function isNodeVisualInsideBox(node: EnrichedAstNode, box: AstPosition): {inside: boolean; overlap: boolean} {
    return isNodeInsideCore(node.visualPosition || node.position, box);
}

export function isNodeInsideBox(node: EnrichedAstNode, box: AstPosition): {inside: boolean; overlap: boolean} {
    return isNodeInsideCore(node.position, box);
}

export function isNodeInsideCore(position: AstPosition, box: AstPosition): {inside: boolean; overlap: boolean} {
    const tl = isPointInsideNode(getTLPoint(position), box);
    const tr = isPointInsideNode(getTRPoint(position), box);
    const bl = isPointInsideNode(getBLPoint(position), box);
    const br = isPointInsideNode(getBRPoint(position), box);

    const overlaps = [tl, tr, bl, br].reduce(
        (memo, current) => {
            memo[current]++;
            return memo;
        },
        {
            INSIDE: 0,
            OUTSIDE: 0,
            ON_BORDER: 0,
        }
    );

    if (overlaps.ON_BORDER === 4) {
        return {inside: true, overlap: false};
    }

    if (overlaps.OUTSIDE === 0) {
        return {inside: true, overlap: false};
    }

    if (overlaps.INSIDE > 0 && overlaps.OUTSIDE > 0) {
        return {inside: false, overlap: true};
    }

    const union = unionPositions(box, position);

    if (union.height > 0 && union.width > 0) {
        return {inside: false, overlap: true};
    }

    return {inside: false, overlap: false};
}

function isPointInsideNode({x, y}: {x: number; y: number}, position: AstPosition): 'OUTSIDE' | 'INSIDE' | 'ON_BORDER' {
    // is the point outside, inside or on the border of the box.

    const xLanded = x >= position.absoluteX && x <= position.absoluteX + position.width;

    const yLanded = y >= position.absoluteY && y <= position.absoluteY + position.height;

    if ((x === position.absoluteX || x === position.absoluteX + position.width) && yLanded) {
        return 'ON_BORDER';
    }

    if ((y === position.absoluteY || y === position.absoluteY + position.height) && xLanded) {
        return 'ON_BORDER';
    }

    if (xLanded && yLanded) {
        return 'INSIDE';
    }

    return 'OUTSIDE';
}

// boxes should overlap and this will return the box of there union.
export function unionPositions(a: AstPosition, b: AstPosition): AstPosition {
    if (a.absoluteX > b.absoluteX) {
        const temp = a;
        a = b;
        b = temp;
    }
    const newRect: AstPosition = {
        absoluteX: b.absoluteX,
        absoluteY: Math.max(a.absoluteY, b.absoluteY),
        width: Math.min(a.absoluteX + a.width - b.absoluteX, b.width),
        height: Math.min(a.absoluteY + a.height, b.absoluteY + b.height) - Math.max(a.absoluteY, b.absoluteY),
    };
    return newRect;
}

export function intersectPositions(a: AstPosition, b: AstPosition): AstPosition {
    const r1 = {x1: a.absoluteX, x2: a.absoluteX + a.width, y1: a.absoluteY, y2: a.absoluteY + a.height};
    const r2 = {x1: b.absoluteX, x2: b.absoluteX + b.width, y1: b.absoluteY, y2: b.absoluteY + b.height};
    const result = getIntersectingRectangle(r1, r2);

    if (!result) {
        return {absoluteX: 0, absoluteY: 0, width: 0, height: 0};
    }

    return {
        absoluteX: result.x1,
        absoluteY: result.y1,
        width: result.x2 - result.x1,
        height: result.y2 - result.y1,
    };
}

const getIntersectingRectangle = (r1, r2) => {
    [r1, r2] = [r1, r2].map((r) => {
        return {
            x: [r.x1, r.x2].sort((a, b) => a - b),
            y: [r.y1, r.y2].sort((a, b) => a - b),
        };
    });

    const noIntersect = r2.x[0] > r1.x[1] || r2.x[1] < r1.x[0] || r2.y[0] > r1.y[1] || r2.y[1] < r1.y[0];

    return noIntersect
        ? false
        : {
              x1: Math.max(r1.x[0], r2.x[0]), // _[0] is the lesser,
              y1: Math.max(r1.y[0], r2.y[0]), // _[1] is the greater
              x2: Math.min(r1.x[1], r2.x[1]),
              y2: Math.min(r1.y[1], r2.y[1]),
          };
};

export const positionsAreEqual = (a: AstPosition, b: AstPosition) =>
    a.absoluteX === b.absoluteX && a.absoluteY === b.absoluteY && a.height === b.height && a.width === b.width;
